import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Divider,
  Box,
  Typography,
  Button,
  Stack,
  DialogTitle,
  Tab,
  Tabs,
  DialogContent,
  Tooltip,
  IconButton,
  DialogActions,
  Snackbar,
  Dialog,
  CardContent,
  Paper,
  Card
} from '@material-ui/core';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LRUSummaryTable from '../rack/details/LRUSummaryTable';
import ComponentLoadHistory from '../rack/details/loading/ComponentLoadHistory';
import KitLoadHistory from '../rack/details/loading/KitLoadHistory';

const YAML = require('yaml');

SnapshotSummary.propTypes = {
  data: PropTypes.object,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func
};
SnapshotSummary.defaultProps = {
  data: {}
};

export default function SnapshotSummary({ data, isOpen, setIsOpen }) {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(YAML.stringify(data, null, 2));
    setCopied(true);
  };
  const handleCloseModal = () => {
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth>
      <DialogTitle>Snapshot Details</DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Detail" />
        <Tab label="YAML View" />
      </Tabs>

      <DialogContent dividers>
        <Box sx={{ maxHeight: 700, overflowY: 'auto' }}>
          {tab === 0 && <BeautyView data={data} />}
          {tab === 1 && (
            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Copy YAML">
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                  onClick={handleCopy}
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <YamlView data={data} />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button data-testid="close-template-preview" onClick={handleCloseModal}>
          Close
        </Button>
      </DialogActions>

      <Snackbar
        open={copied}
        autoHideDuration={1500}
        handleCloseModal={() => setCopied(false)}
        onClose={() => setCopied(false)}
        message="YAML copied!"
      />
    </Dialog>
  );
}

YamlView.propTypes = {
  data: PropTypes.object
};

function YamlView({ data }) {
  const snapShotYAML = {
    rack: {
      snapshotDetails: {
        name: data.name
      }
    }
  };
  return (
    <Box
      component="pre"
      sx={{
        backgroundColor: '#0f172a',
        color: '#e5e7eb',
        p: 2,
        borderRadius: 1,
        fontSize: 13,
        minHeight: 650,
        overflow: 'auto'
      }}
    >
      {YAML.stringify(snapShotYAML, null, 2)}
    </Box>
  );
}

BeautyView.propTypes = {
  data: PropTypes.object
};
export function BeautyView({ data }) {
  if (data) {
    return (
      <Card>
        <CardContent>
          <Grid component={Paper} container alignItems="center" pt={2} spacing={2}>
            <Grid item>
              <Stack spacing={1.5}>
                <Row label="Name" value={data?.name} />
                <Row label="Description" value={data?.description} />
                <Row label="Identity" value={data?.provisioner_identity || 'Global'} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
        <CardContent>
          <Section title="LRUs">
            <Box marginTop={2}>
              <LRUSummaryTable data={data} />
            </Box>
          </Section>
        </CardContent>
        <CardContent>
          <Section title="Loading History">
            <KitLoadHistory rackDetails={data?.rack_details} />
            <ComponentLoadHistory rackDetails={data?.rack_details} />
          </Section>
        </CardContent>
      </Card>
    );
  }
  return null;
}

Section.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node
};

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
      <Divider sx={{ my: 1 }} />
      {children}
    </Box>
  );
}

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

function Row({ label, value }) {
  return (
    <Stack direction="row" sx={{ gap: 1 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        {label}:
      </Typography>
      <Typography variant="subtitle2" fontWeight={400}>
        {value}
      </Typography>
    </Stack>
  );
}
